import { useAuth } from "@/auth/AuthProvider";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Shield,
  User,
} from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
const KEYCLOAK_ENABLED =
  !!KEYCLOAK_URL &&
  !KEYCLOAK_URL.includes(".invalid") &&
  !KEYCLOAK_URL.includes("placeholder");

export function Header() {
  const navigate = useNavigate();
  const { authenticated, user, roles, login, logout } = useAuth();
  const isAdmin = roles.includes("echorai-admin");

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 shadow-sm backdrop-blur-md">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between px-3 py-4 sm:p-6 lg:px-8"
      >
        <Link
          to="/"
          aria-label="Echorai — startsida"
          className="flex items-center min-w-0 shrink rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src="/echorai.svg"
            alt=""
            aria-hidden="true"
            className="size-8 sm:size-12"
          />
          <div className="flex flex-col px-2">
            <span className="text-base sm:text-xl font-semibold tracking-tight bg-linear-to-r from-foreground to-cyan-500 inline-block text-transparent bg-clip-text">
              Echorai
            </span>
            <span className="hidden sm:inline text-sm text-muted-foreground">
              Control Your Digital Echo
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Badge className="bg-cyan-500 hover:bg-cyan-500 text-white text-xs sm:text-sm lg:px-2 border-transparent">
            ✨ BETA startad ✨
          </Badge>
          <ThemeToggle />
          {KEYCLOAK_ENABLED &&
            (authenticated ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden sm:inline-flex"
                  onClick={() => navigate({ to: "/dashboard" })}
                >
                  <LayoutDashboard className="size-4" />
                  Min Dashboard
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:inline-flex"
                    onClick={() => navigate({ to: "/admin" })}
                  >
                    <Shield className="size-4" />
                    Admin
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Användarmeny">
                      <Menu className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {user?.name && (
                      <>
                        <DropdownMenuLabel className="flex items-center gap-2">
                          <User className="size-4" />
                          {user.name}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      className="sm:hidden"
                      onClick={() => navigate({ to: "/dashboard" })}
                    >
                      <LayoutDashboard className="size-4" />
                      Min Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="size-4" />
                      Logga ut
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={login}>
                <LogIn className="size-4" />
                <span className="hidden sm:inline">Logga in</span>
              </Button>
            ))}
        </div>
      </nav>
    </header>
  );
}
